/**
 * journey.ts — the guest (pre-account) journey gateway calls.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the journey routes are all @Public/guest (no token). The gateway
 * enforces everything; the SPA renders what comes back. Shapes confirmed live
 * against the stage gateway.
 */
import { get, post, request } from './client';
import { LEAD_READINESS_PATH } from './types';
import type {
  AnswerSubmitRequest,
  AssessmentAccountBootstrap,
  AssessmentCallbackStatus,
  AssessmentCreateRequest,
  AssessmentProgress,
  AssessmentResultSummary,
  AssessmentSubmitResult,
  CallbackCreateRequest,
  CallbackLeadStatus,
  CheckoutTokenMint,
  ClinicPublicConfig,
  EvaluationResult,
  GuestMarketingConsentWrite,
  LeadCaptureRequest,
  LeadReadinessRequest,
  LeadResume,
  MarketingConsentState,
  MarketingPreferencesWrite,
  PhotoUploadGrant,
  QuestionSetPublic,
  ResumeGrantExchange,
} from './types';

/** GET /v1/clinic/clinics/:slug → vetted, R28-clean public clinic config. */
export function getClinic(slug: string): Promise<ClinicPublicConfig> {
  return get(`/v1/clinic/clinics/${encodeURIComponent(slug)}`);
}

/** POST /v1/clinic/leads — Step-0 lead-first capture. */
export function createLead(body: LeadCaptureRequest): Promise<{ leadId: string }> {
  return post('/v1/clinic/leads', body);
}

/**
 * POST /v1/clinic/leads/intent — attach the qualification screen's readiness
 * answer to a lead that has already been captured (2026-09-12).
 *
 * FIRE-AND-FORGET AT EVERY CALL SITE. This is funnel plumbing: it records a
 * commercial segment and nothing the patient is owed, so a failure must never
 * reach them and must never hold up the questionnaire. It is also why the
 * release order across the three repos does not matter — a portal deployed
 * ahead of the gateway route simply records nothing for a few minutes.
 *
 * The server returns an empty 200 to EVERY caller — unknown lead, expired lead,
 * already-answered lead and success are indistinguishable on purpose, so the
 * route cannot be used as an oracle for "does this leadId exist".
 */
export function recordLeadReadiness(body: LeadReadinessRequest): Promise<Record<string, never>> {
  return post(LEAD_READINESS_PATH, body);
}

/**
 * POST /v1/clinic/leads/resume — the Webflow-widget hand-off read (guest).
 * A POST (not GET) so the leadId stays out of API URLs/access logs; the portal
 * side keeps only the opaque `?lead=` param in its own URL (no PII). Trust
 * model is possession of the unguessable leadId — same class as `?resume=`.
 * 404 = unknown/purged lead (the caller falls back to the normal Step-0 start).
 */
export function resumeLead(leadId: string): Promise<LeadResume> {
  return post('/v1/clinic/leads/resume', { leadId });
}

/** POST /v1/clinic/assessments — create the draft assessment. */
export function createAssessment(body: AssessmentCreateRequest): Promise<{ assessmentId: string }> {
  return post('/v1/clinic/assessments', body);
}

/** Guest write lanes require `?clinicSlug=` (clinic-service 2026-08-14). */
function clinicScopePath(path: string, clinicSlug: string): string {
  return `${path}?clinicSlug=${encodeURIComponent(clinicSlug)}`;
}

/** GET /v1/clinic/assessments/:id/questions — server-redacted question set. */
export function getQuestions(assessmentId: string): Promise<QuestionSetPublic> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/questions`);
}

/**
 * POST /v1/clinic/assessments/:id/answers?clinicSlug= — one answer, one verdict.
 * The slug is REQUIRED on this guest write; omitting it 400s `clinicSlug: Required`.
 */
export function submitAnswer(
  assessmentId: string,
  clinicSlug: string,
  body: AnswerSubmitRequest,
): Promise<EvaluationResult> {
  return post(
    clinicScopePath(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/answers`, clinicSlug),
    body,
  );
}

/**
 * POST /v1/clinic/assessments/:id/photo-upload?clinicSlug= — signed PUT grant
 * (or stub). Same required clinic scope as answers/submit.
 */
export function grantPhotoUpload(assessmentId: string, clinicSlug: string): Promise<PhotoUploadGrant> {
  return post(
    clinicScopePath(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/photo-upload`, clinicSlug),
    {},
  );
}

/** Fallback ceiling — only used if a grant omits `maxBytes` (older/malformed). */
const SIGNED_PUT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * PUT to a clinic-service V4 signed URL. Stub grants resolve immediately.
 *
 * The signature pins `Content-Type` and `x-goog-content-length-range` — both
 * MUST be sent on the PUT or GCS returns 400. Weight photos are always
 * `image/jpeg`; deal attachments pass the type declared at grant time.
 *
 * The size ceiling comes from the grant (`maxBytes`, the exact value
 * clinic-service signed), NOT a mirrored constant — otherwise a server-side
 * ceiling bump re-signs with a new range while the portal keeps sending the old
 * one, 400ing every PUT. The constant is only a fallback for a grant that omits
 * the field.
 *
 * ⚠️ WHY THE WEIGHT-PHOTO DEFAULT STAYS `image/jpeg` EVEN THOUGH THE PICKERS
 * ACCEPT `image/*` (checked against clinic-service, 2026-08-27). It looks like a
 * bug — a patient picks a PNG or a HEIC and we declare it a JPEG — but the
 * declaration is not ours to make. `PhotoStorage.grantUpload` signs the V4 URL
 * with a HARDCODED `contentType: 'image/jpeg'`, and GCS rejects any PUT whose
 * Content-Type differs from the signed one. Sending the picked file's real type
 * would therefore 400 every non-JPEG upload, which is strictly worse than
 * storing a correctly-uploaded object under a slightly wrong label: the bytes
 * are whatever the patient picked, browsers sniff images anyway, and the
 * clinician's gallery renders it. `grantAttachmentUpload` (deal attachments) is
 * the lane that DOES sign the caller's type, which is why this parameter exists
 * at all. Passing the real type here needs clinic-service to accept and sign a
 * declared content type on the photo lane first — a server change, not a
 * one-line fix here.
 */
export async function uploadPhoto(
  grant: PhotoUploadGrant,
  file: File,
  contentType: string = 'image/jpeg',
): Promise<void> {
  if (grant.stub || !grant.uploadUrl) return;
  const response = await fetch(grant.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-goog-content-length-range': `1,${grant.maxBytes ?? SIGNED_PUT_MAX_BYTES}`,
    },
    body: file,
  });
  if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
}

/**
 * Wave 11 — the signed-in PATIENT's deferred weight photo, added AFTER
 * submission ("I'll add this before dispatch"). Unlike the guest
 * `grantPhotoUpload(:id, clinicSlug)` above, these are AUTHED (session cookie) and carry NO
 * assessment id — the clinic-service self-scopes by the verified principal to
 * the caller's OWN latest submitted assessment. See clinic `mine/weight-photo`.
 */
export function getWeightPhotoStatus(): Promise<{ photoPending: boolean }> {
  return get('/v1/clinic/assessments/mine/weight-photo', true);
}

/** Grant a signed PUT for the caller's OWN deferred weight photo (or `{stub:true}`). */
export function grantMyWeightPhotoUpload(): Promise<PhotoUploadGrant> {
  return post('/v1/clinic/assessments/mine/weight-photo', {}, { auth: true });
}

/**
 * POST /v1/clinic/assessments/:id/submit?clinicSlug= — requires the full
 * consent bundle. Same required clinic scope as answers/photo-upload.
 */
export function submitAssessment(assessmentId: string, clinicSlug: string): Promise<AssessmentSubmitResult> {
  return request(
    clinicScopePath(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/submit`, clinicSlug),
    {
      method: 'POST',
      body: {},
    },
  );
}

/**
 * POST /v1/clinic/assessments/:id/checkout-token — mint assessment-bound guest
 * checkout grant (plan → delivery → payment before account). Same token class
 * as re-entry (peek during checkout; consume at register).
 *
 * The server may send more than the grant on this response; nothing here reads
 * it. See {@link CheckoutTokenMint} for the one field that was deliberately
 * dropped rather than left declared.
 */
export function mintCheckoutToken(
  assessmentId: string,
  clinicSlug: string,
): Promise<CheckoutTokenMint> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/checkout-token`, {
    clinicSlug,
  });
}

/**
 * POST /v1/clinic/assessments/:id/plan-viewed — tell the server the patient
 * opened their plan link (T-1, 2026-09-10). FIRE AND FORGET.
 *
 * WHY THE CLIENT HAS TO SAY SO. The plan link is a FRAGMENT
 * (`/weightloss#resume=<id>`) precisely so the assessment id never reaches an
 * access log, a Referer header or GTM's `page_location` — which means the
 * server cannot see the id on the page load and no request of its own implies
 * the open. This POST is the only way the fact exists at all.
 *
 * It answers the one question the conversion review could not: of the patients
 * a clinician approves, how many ever SEE their recommendation? Without it a
 * dead notification and a price objection are the same number, and those have
 * completely different fixes.
 *
 * ⚠️ EVERY CALLER MUST IGNORE THE RESULT, success and failure alike — the same
 * rule the plan-link copy receipt follows. The server answers 204 to
 * everything (unknown id, purged, no plan issued, already stamped) so there is
 * no outcome to read, and the patient must never be shown an error about a
 * measurement they did not ask us to take. `void`, not `Promise<void>`, would
 * be a lie about the network; the swallow belongs at the call site so a
 * rejected promise cannot become an unhandled one.
 *
 * NO BODY and no auth. The route is on the gateway's @Public allowlist and
 * stamps an anonymous principal even for a signed-in patient, so a bearer
 * would be dropped in transit — passing one would only suggest it mattered.
 */
/**
 * POST /v1/clinic/assessments/:id/drop-off-reason — why this approved patient
 * is not paying (T-2). The reason ONLY: the server stamps where it was asked,
 * and answers 204 whatever happens (no existence oracle), so the only failure
 * this can surface is a transport one.
 *
 * Unlike the plan-open beacon this IS awaited and its failure IS shown: the
 * patient chose to tell us, so a silent drop would be lying to them.
 */
export function recordDropOffReason(assessmentId: string, reason: string): Promise<void> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/drop-off-reason`, { reason });
}

export function recordPlanViewed(assessmentId: string): Promise<void> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/plan-viewed`, {});
}

/**
 * POST /v1/clinic/assessments/resume-exchange {token} — trade a `#resume=`
 * GRANT (the short-TTL ReentryToken the unlock lanes mint) for the assessment
 * id today's plan-link path runs on. @Public, guest-throttled, and the token
 * rides ONLY in the body (the gateway 400s the lane on any query string).
 *
 * verifyResume PEEKS the token rather than consuming it — a reload inside the
 * 15-minute window must not strand the patient; the register BIND stays the
 * consuming step.
 *
 * WHY THIS IS ACCEPTABLE TO EXPOSE (stated plainly, per the pinned addendum):
 * this hands the raw assessment id to the GRANT holder for the grant's
 * 15-minute life — a bounded, per-click, throttle-capped upgrade of exactly
 * the capability the existing plan-invite email exposes INDEFINITELY today
 * (its `#resume=<assessmentId>` never expires). The durable close is the
 * estate's signed-guest-capability rework; until then, link revocation and
 * expiry kill this chain upstream.
 *
 * A token the server will not honour is a generic 404 — the caller renders
 * the existing "This link has expired" screen and never guesses why.
 */
export function exchangeResumeGrant(token: string): Promise<ResumeGrantExchange> {
  return post('/v1/clinic/assessments/resume-exchange', { token });
}

/**
 * GET /v1/clinic/assessments/:id/marketing-consent?clinicSlug= — the person's OWN
 * stored marketing state (2026-09-11). Guest, no auth, the same
 * possession-of-the-id trust model as `/progress`.
 *
 * ONE CALLER, AND IT USES ONE FIELD: the q15 "Keeping in touch" block's
 * WhatsApp OFFER CHECK (`marketing/journeyConsent.ts#useWhatsappOffer`), fired
 * once when that consent screen mounts. It asks a single question — does this
 * lead hold a UK mobile (`whatsappAvailable`)? — because the approved WhatsApp
 * wording (`mc-2026-09-11-d1`) says "on the mobile number I gave you" and may
 * only be shown to someone who gave one. The stored `email` / `whatsapp` are
 * deliberately NEVER used: both boxes render unticked whatever the server holds
 * (a pre-ticked box is not consent — PECR reg. 22 / UK GDPR Art 7), and the lane
 * is grants-only, so an existing grant is simply left as it is.
 *
 * IT NOW ANSWERS A DRAFT. clinic-service (`fix/consent-step-source`) serves this
 * read for an in-progress, not-hard-stopped DRAFT as well as after an eligible
 * submit. The WRITE below is unchanged: post-submit and eligible only. A
 * clinic-service deploy that predates that change 404s the draft read.
 *
 * A 404 — or ANY failure — means UNKNOWN, and unknown means WhatsApp is NOT
 * offered: the block shows the email box alone and paints no error. A 404 covers
 * every "not for you" case at once (unknown, purged, wrong clinic, no lead,
 * hard-stopped, an older clinic-service); a caller cannot tell them apart and
 * must not try.
 */
export function getMarketingConsent(assessmentId: string, clinicSlug: string): Promise<MarketingConsentState> {
  return get(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/marketing-consent?clinicSlug=${encodeURIComponent(clinicSlug)}`,
  );
}

/**
 * POST /v1/clinic/assessments/:id/marketing-consent — record the tick made on
 * the questionnaire's consent step (since 2026-09-23; the Result screen before
 * that), plus the wording version. Its one caller is
 * `marketing/journeyConsent.ts#postJourneyMarketingGrant`, fired once and never
 * awaited right after a SUCCESSFUL submit — the server refuses a draft — and it
 * only ever names a channel the block SHOWED, as `true`: GRANTS ONLY. The draft
 * read above decides only WHETHER the WhatsApp box is offered; its stored values
 * are never used as a prior state, so an unticked box is still a default and
 * never a withdrawal. clinic-service stamps the ledger rows `journey_consent_step`.
 *
 * A channel left out of the body is left exactly as it is; the server writes a
 * ledger row only for a channel that actually changed, and answers the state
 * AFTER the write. `marketing/consentChannels.ts` decides which channels
 * qualify, and why a hidden box may never be one of them.
 *
 * The slug rides in the BODY here, not the query string — that is this route's
 * contract, unlike the answers/submit lanes above.
 */
export function saveMarketingConsent(
  assessmentId: string,
  body: GuestMarketingConsentWrite,
): Promise<MarketingConsentState> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/marketing-consent`, body);
}

/**
 * POST /v1/clinic/marketing/preferences/view — what the emailed link's holder
 * has chosen. PUBLIC (no session): the capability is the signed token, which
 * travels in the BODY and never in a URL, so it cannot reach an access log from
 * this request. 404 `{message:'Unknown link'}` for a token the server will not
 * honour.
 */
export function viewMarketingPreferences(token: string): Promise<MarketingConsentState> {
  return post('/v1/clinic/marketing/preferences/view', { token });
}

/** POST /v1/clinic/marketing/preferences — save both choices; answers the state AFTER the write. */
export function saveMarketingPreferences(body: MarketingPreferencesWrite): Promise<MarketingConsentState> {
  return post('/v1/clinic/marketing/preferences', body);
}

/** POST /v1/clinic/callbacks — the free callback lane (lead pipeline). */
export function createCallback(body: CallbackCreateRequest): Promise<{ callbackId?: string }> {
  return post('/v1/clinic/callbacks', body);
}

/**
 * GET /v1/clinic/assessments/:assessmentId/progress?clinicSlug= — the
 * navigation-resilience restore read (guest, no auth — same posture as every
 * other journey.ts call). Strict, PHI-free response: JourneyPage uses it
 * SOLELY to work out which stage a refreshed `?assessment=<uuid>` should land
 * on; it never renders answers from it. Callers must distinguish failures:
 * 404 = genuine unknown (fail soft to capture); network/5xx = retain
 * `?assessment=` and retry — never invent a stage from a caught error
 * (see JourneyPage).
 */
export function getAssessmentProgress(assessmentId: string, clinicSlug: string): Promise<AssessmentProgress> {
  return get(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/progress?clinicSlug=${encodeURIComponent(clinicSlug)}`,
  );
}

/**
 * POST /v1/clinic/assessments/result-summary — Tier-2 guest ResultScreen hydrate
 * (capture H/W + BMI). Option C: the signed re-entry token rides in the BODY, so
 * the assessment id never appears in a URL/log/Referer; the id is derived
 * server-side from the token. Reached only on a RETURN visit after the emailed
 * code is verified (see verifyRestoreCode). The IN-FLOW ResultScreen — same
 * session, right after submit — hydrates from the submit response instead, so
 * there is no cold token read mid-journey. Failures → Retry.
 */
export function getAssessmentResultSummary(reEntryToken: string): Promise<AssessmentResultSummary> {
  return post('/v1/clinic/assessments/result-summary', { reEntryToken });
}

/**
 * POST /v1/clinic/assessments/account-bootstrap — Tier-2 guest Create-account
 * hydrate (Lead email + DOB). Re-entry token in the body (see result-summary).
 * Missing fields arrive as null. Failures → Retry.
 */
export function getAssessmentAccountBootstrap(reEntryToken: string): Promise<AssessmentAccountBootstrap> {
  return post('/v1/clinic/assessments/account-bootstrap', { reEntryToken });
}

/**
 * POST /v1/clinic/assessments/callback-status — Tier-2 guest StopScreen restore
 * boolean. Re-entry token in the body. Status-only — never name/phone/
 * preferredTime. Failures → Retry (never assume "not submitted").
 */
export function getAssessmentCallbackStatus(reEntryToken: string): Promise<AssessmentCallbackStatus> {
  return post('/v1/clinic/assessments/callback-status', { reEntryToken });
}

/**
 * POST /v1/clinic/assessments/:assessmentId/restore/request-code — Tier-2 OTP.
 * Emails a 6-digit code to the assessment's registered email. GENERIC 200
 * (never reveals whether the id/email exists), so the caller shows a neutral
 * "if that email is on file we've sent a code" regardless. Fail-soft.
 */
export function requestRestoreCode(assessmentId: string): Promise<{ ok: true }> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/restore/request-code`, {});
}

/**
 * POST /v1/clinic/assessments/:assessmentId/restore/verify — Tier-2 OTP verify.
 * On the first correct 6-digit code it returns the short-lived re-entry token
 * (in `reEntryToken`) to carry in state for the sensitive reads + account
 * creation. Any miss (wrong / expired / attempt-capped) → 400 (the UI shows
 * "that code didn't match — check it or request a new one").
 */
export function verifyRestoreCode(assessmentId: string, code: string): Promise<{ reEntryToken: string }> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/restore/verify`, { code });
}

/**
 * GET /v1/clinic/callbacks/status?clinicSlug=&leadId= — guest standalone
 * /callback restore when `?lead=` is present. Failures → Retry (never assume
 * not submitted).
 */
export function getCallbackStatusByLead(clinicSlug: string, leadId: string): Promise<CallbackLeadStatus> {
  return get(
    `/v1/clinic/callbacks/status?clinicSlug=${encodeURIComponent(clinicSlug)}&leadId=${encodeURIComponent(leadId)}`,
  );
}
